import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { IconWhatsApp } from './icons';
import { useLanguage } from '../contexts/LanguageContext';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { LEAD_FORM_ID } from '../utils/scroll';

/**
 * Persistent WhatsApp entry point.
 * - Mobile: full-width sticky bar at the bottom.
 * - Desktop: floating button in the bottom-right corner.
 *
 * Both appear only after the hero scrolls out and hide while the lead form is in
 * view, so they can never cover fields, validation messages, or the submit button.
 * Both open the single shared WhatsApp line directly, with the message prefilled.
 */
export function WhatsAppCTA() {
  const {
    t
  } = useLanguage();
  const whatsapp = useWhatsApp();
  const [pastHero, setPastHero] = useState(false);
  const [blockedInView, setBlockedInView] = useState(false);
  const reduce = useReducedMotion();
  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hidden while the lead form OR the closing block is on screen: the closing block
  // already has its own WhatsApp button, and two identical green buttons stacked on
  // top of each other is noise, not emphasis.
  useEffect(() => {
    const nodes = [LEAD_FORM_ID, 'final-cta'].map(id => document.getElementById(id)).filter((node): node is HTMLElement => Boolean(node));
    if (!nodes.length) return;
    const visible = new Set<Element>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visible.add(entry.target);else visible.delete(entry.target);
      });
      setBlockedInView(visible.size > 0);
    }, {
      threshold: 0
    });
    nodes.forEach(node => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  // Hidden entirely when no number is configured — never a dead control.
  const show = whatsapp.available && pastHero && !blockedInView;
  const motionProps = reduce ? {} : {
    initial: {
      opacity: 0,
      y: 15
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 15
    },
    transition: {
      duration: 0.3
    }
  };
  return <AnimatePresence data-id="element-417" data-mp="106e4hh9f8n9j">
      {show && <>
          {/* Mobile sticky bar */}
          <motion.div key="whatsapp-bar" {...motionProps} className="fixed inset-x-0 bottom-0 z-40 border-t border-camel/40 bg-white/95 p-3 backdrop-blur-md md:hidden" data-id="element-418" data-mp="v9nosv1j3ton">
            <button type="button" onClick={() => whatsapp.open('sticky-mobile')} className="flex min-h-[54px] w-full items-center justify-center gap-2.5 bg-whatsapp px-5 text-[13px] font-bold uppercase tracking-[0.14em] text-golddeep shadow-[0_-2px_20px_-12px_rgba(0,0,0,0.6)] active:scale-[0.99]" data-id="element-419" data-mp="l9wqsjdrewjt">
              <IconWhatsApp className="h-[18px] w-[18px]" data-id="element-420" data-mp="1qh6dr8gnnjwe" />
              {t('sticky.cta')}
            </button>
          </motion.div>

          {/* Desktop floating button */}
          <motion.div key="whatsapp-fab" {...motionProps} className="fixed bottom-6 right-6 z-40 hidden md:block" data-id="element-421" data-mp="1vezli5xlwlys">
            <button type="button" onClick={() => whatsapp.open('floating-desktop')} aria-label={t('sticky.cta')} className="flex min-h-[58px] items-center gap-3 rounded-full bg-whatsapp pl-5 pr-6 text-[13px] font-bold uppercase tracking-[0.14em] text-golddeep shadow-[0_14px_34px_-14px_rgba(37,211,102,0.85)] transition-all duration-300 hover:-translate-y-[2px] hover:shadow-[0_18px_40px_-14px_rgba(37,211,102,0.95)]" data-id="element-422" data-mp="2562j2xrbuitb">
              <IconWhatsApp className="h-5 w-5" data-id="element-423" data-mp="ot5blz5gd3o3" />
              <span data-id="element-424" data-mp="2f9hb3e5za2rn">{t('sticky.cta')}</span>
            </button>
          </motion.div>
        </>}
    </AnimatePresence>;
}